/**
 * DataTable — one table, built right by construction.
 *
 * Nine rules styled a `th` across seven files and the browser painted FOUR distinct table
 * styles, two of them uppercase (AUD-7 §1.3 / IM-3, F-80). The CSS half of that is fixed in
 * `theme.css`'s single `.table` rule; this is the markup half, so the next table cannot be
 * built wrong:
 *
 *   · always inside `.table-scroll` — AUD-7 §6.13's rule, now impossible to forget;
 *   · always `scope="col"` on every header;
 *   · an icon-only column gets a REAL, visually-hidden name via `srOnlyHeader`, not an
 *     `aria-label` on an empty `<th>` (several screen readers ignore that);
 *   · numeric columns get `tabular-nums` and right alignment together, because a
 *     right-aligned column with proportional digits still fails to line up by place value.
 *
 * Deliberately NOT `role="grid"`. `grid` promises a two-dimensional widget with roving focus
 * and arrow-key cell navigation; this implements neither, and announcing it would be a lie.
 * WS-B reasoned exactly this for `TemplateTable` and the reasoning survives the migration —
 * when roving focus is added, `role="grid"` goes on with it.
 */
import type { ReactNode } from "react";

export type Column<T> = {
  key: string;
  header: string;
  /** Column width, e.g. "1%" for a checkbox gutter. */
  width?: string;
  align?: "left" | "right";
  /** Right-aligns AND applies tabular figures. One decision, not two. */
  numeric?: boolean;
  /** A real accessible name that is not painted — for icon-only / control columns. */
  srOnlyHeader?: boolean;
  /**
   * Extra class on the `<td>`s of this column — the CELLS, not the header.
   * They are separate on purpose: `.tpl-table__actions` is a sticky, right-aligned cell and
   * `.tpl-table__actions-col` is a 1%-wide column, and putting one class on both would make
   * `querySelector(".tpl-table__model")` find the header instead of the first cell.
   */
  className?: string;
  /** Extra class on the `<th>` only — column sizing that must not hit every cell. */
  headerClassName?: string;
  /**
   * Per-row attributes on this column's `<td>` — a `title` that must sit on the CELL rather
   * than on a span inside it, a `data-*` the row's own props cannot express. `title` on the
   * cell is load-bearing for the Model column: it is what keeps the full label recoverable
   * when the column is squeezed, and a browser only shows it for the element it is on.
   */
  cellProps?: (row: T) => Record<string, string | undefined>;
  render: (row: T) => ReactNode;
};

export function SrOnly({ children, className = "sr-only" }: { children: ReactNode; className?: string }) {
  return <span className={className}>{children}</span>;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  className = "",
  testId,
  caption,
  rowProps,
  head,
  empty,
  srOnlyClassName = "sr-only",
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Extra classes on the `<table>`, e.g. "tpl-table templates__list". */
  className?: string;
  testId?: string;
  /** A visually-hidden caption naming what the table lists. */
  caption?: string;
  /** Per-row attributes — data-on-device, data-status, data-testid, … */
  rowProps?: (row: T) => Record<string, string | undefined>;
  /** Extra attributes on the `<table>` itself (data-view, aria-*). */
  head?: Record<string, string | undefined>;
  /** Rendered INSTEAD of the table when `rows` is empty. */
  empty?: ReactNode;
  /**
   * The visually-hidden class on `srOnlyHeader` columns. The global utility is `.sr-only`;
   * a caller passes a legacy alias here when an existing suite selects on it. A test
   * asserting a class IS a contract, so the class stays and the utility is added beside it
   * rather than renaming anything (WS-B's `template-index.test.tsx` selects `.ctpl-sr-only`).
   */
  srOnlyClassName?: string;
}) {
  if (rows.length === 0 && empty) return <>{empty}</>;
  return (
    <div className="table-scroll">
      <table className={`table ${className}`.trim()} data-testid={testId} {...head}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={[c.headerClassName, c.numeric || c.align === "right" ? "num" : undefined]
                  .filter(Boolean)
                  .join(" ")}
                style={c.width ? { width: c.width } : undefined}
              >
                {c.srOnlyHeader ? <SrOnly className={srOnlyClassName}>{c.header}</SrOnly> : c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} {...(rowProps?.(row) ?? {})}>
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={[c.className, c.numeric || c.align === "right" ? "num" : undefined]
                    .filter(Boolean)
                    .join(" ")}
                  {...(c.cellProps?.(row) ?? {})}
                >
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
